import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { CatalogMaterial } from "../../../models/CatalogMaterial";
import { useMaterialsOptionsFunctions } from "../hooks/useMaterialsOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { MaterialModal } from "./MaterialModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogMaterialFromAPI: Function;
  isLoading: boolean;
  materialId: String | undefined;
};

export const TableMaterial = ({
  getCatalogMaterialFromAPI,
  isLoading,
  materialId,
}: Props) => {
  const navigate = useNavigate();

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [material, setMaterial] = useState<CatalogMaterial | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewMaterial = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setMaterial(null);
    navigate(`/configuration/steel-materials/new`);
  };

  const { getMenuOptions } = useMaterialsOptionsFunctions({
    setMaterial,
    setShowModal,
    handleNewMaterial,
  });
  const { rows, columnDefs } = useTableData({
    setMaterial,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setMaterial(data.Id);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setMaterial(event.data as CatalogMaterial);
      setShowModal("edit");
      navigate(
        `/configuration/steel-materials/${
          (event.data as CatalogMaterial).MaterialId
        }`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/steel-materials/${
          (event.data as CatalogMaterial).MaterialId
        }`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "materialContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setMaterial(null);
    navigate(`/configuration/steel-materials/`);
  };

  useEffect(() => {
    if (materialId) {
      if (materialId === "new") {
        handleNewMaterial();
      } else {
        let materialNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogMaterial).MaterialId === Number(materialId)) {
            materialNode = n;
            return true;
          }
        });

        if (materialNode!) {
          gridApi?.ensureIndexVisible(materialNode.rowIndex ?? 0, "middle");
          materialNode.setSelected(true);
          setMaterial(materialNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "materialContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "1px 0 10px", fontWeight: 600 }}
          >
            Materials
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogMaterial>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.MaterialId)}
              loading={isLoading}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="MATERIAL" onClick={handleNewMaterial} />
        </Grid>
      </Grid>
      <MaterialModal
        material={material}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogMaterialFromAPI}
      />
    </>
  );
};
